import { Flex, Spinner, Text } from "@radix-ui/themes";
import { useNavigate, useParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Dot,
  DotProps,
  Legend,
  LegendProps,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { DashboardStatsResponse } from "@/shared/api";
import dayjs from "dayjs";
import { CategoricalChartFunc } from "recharts/types/chart/generateCategoricalChart";
import { useLocalStorage } from "react-use";
import { useLastClickedOrder } from "../lib/last-clicked-order-provider";
import { ContentType } from "recharts/types/component/Tooltip";
import {
  NameType,
  ValueType,
} from "recharts/types/component/DefaultTooltipContent";

type PredictionLineBarChartProps = {
  data: DashboardStatsResponse | undefined;
  isFetching: boolean;
  isBarChart?: boolean;
  opacityKey?: string;
  debounce?: number;
  labelToValueMap?: Record<string, string>;
  colorA?: string;
  colorB?: string;
  dataKeyA?: string;
  dataKeyB?: string;
  tooltipContent: ContentType<ValueType, NameType>;
  isChartClickable: (payload: any) => boolean;
};

type OpacityState = Record<string, number>;

export function PredictionLineBarChart({
  data,
  isFetching,
  isBarChart,
  opacityKey = "chart_line_opacity",
  debounce = 2000,
  labelToValueMap,
  colorA = "#3E63DD",
  colorB = "#53B365",
  dataKeyA = "zone_based_f1_score",
  dataKeyB = "pin_based_f1_score",
  tooltipContent,
  isChartClickable,
}: PredictionLineBarChartProps) {
  const navigate = useNavigate();
  const { storeKey } = useParams();
  // Initialize opacity with dynamic keys based on dataKeyA and dataKeyB
  const getInitialOpacity = () => {
    const stored = localStorage.getItem(opacityKey);
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch {
        // Fall back to default if parsing fails
      }
    }
    // Create default opacity for the current data keys
    return {
      [dataKeyA]: 1,
      [dataKeyB]: 1,
    };
  };

  const [opacity, setOpacity] = useLocalStorage<OpacityState>(
    opacityKey,
    getInitialOpacity()
  );
  const { getLastClickedOrderId, setLastClickedOrderId } =
    useLastClickedOrder();

  const renderDot = (props: DotProps & { payload: { order_id: string } }) => {
    const getOverrideProps = () => {
      if (props.payload.order_id !== getLastClickedOrderId()) {
        return undefined;
      }

      return {
        r: 6,
        fill: props.stroke,
        stroke: "#fff",
      };
    };

    return <Dot {...props} {...getOverrideProps()} key={props.key} />;
  };

  const onClickChart: CategoricalChartFunc = (event) => {
    const orderId = event.activePayload?.[0].payload.order_id;

    if (!orderId || !isChartClickable(event)) {
      return;
    }

    setLastClickedOrderId(orderId);
    navigate(`/stores/${storeKey}/dashboard/orders/${orderId}`);
  };

  const onClickLegend: LegendProps["onClick"] = ({ dataKey }) => {
    const typedDataKey = dataKey as string;

    // Get all available data keys from the current opacity state
    const availableKeys = Object.keys(opacity || {});
    const otherKeys = availableKeys.filter((key) => key !== typedDataKey);

    // Check if all other keys are at 0 opacity
    const allOthersHidden = otherKeys.every((key) => opacity?.[key] === 0);

    if (allOthersHidden && opacity?.[typedDataKey] === 1) {
      // If this is the only visible line, show all lines
      const newOpacity = availableKeys.reduce(
        (acc, key) => ({ ...acc, [key]: 1 }),
        {} as OpacityState
      );
      setOpacity(newOpacity);
    } else {
      // Hide all other lines and show only this one
      const newOpacity = availableKeys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: key === typedDataKey ? 1 : 0,
        }),
        {} as OpacityState
      );
      setOpacity(newOpacity);
    }
  };

  const legendFormatter = (value: string) => {
    // Get the data key for this legend value
    const dataKey = labelToValueMap?.[value];
    const isVisible = opacity && dataKey && opacity[dataKey] > 0;

    return (
      <Text
        size="2"
        className={
          isVisible
            ? "font-medium select-none cursor-pointer"
            : "opacity-50 select-none cursor-pointer"
        }
      >
        {value}
      </Text>
    );
  };

  return (
    <Flex className="relative w-full h-[250px] mt-2">
      {isFetching && (
        <Flex
          justify="center"
          align="center"
          className="absolute z-10 w-full h-full bg-white/50"
          gap="2"
        >
          <Spinner />
          <Text size="1">Loading data...</Text>
        </Flex>
      )}
      {!!data && (
        <ResponsiveContainer debounce={debounce} maxHeight={240}>
          {isBarChart ? (
            <BarChart data={data?.results} onClick={onClickChart}>
              <CartesianGrid stroke="#eee" />
              <XAxis
                dataKey="requested_at"
                className="tabular-nums"
                tickFormatter={(value) =>
                  dayjs(value).utc().format("YYYY-MM-DD, HH:mm")
                }
                fontSize={11}
                angle={-45}
                tickLine
                tickMargin={10}
                tick={{ textAnchor: "end" }}
                height={110}
                overflow="visible"
              />
              <YAxis fontSize={12} />
              <Tooltip content={tooltipContent} />
              <Legend
                fontSize={10}
                fontSizeAdjust={0.5}
                onClick={onClickLegend}
                formatter={legendFormatter}
              />
              <Bar
                barSize={4}
                isAnimationActive={false}
                dataKey={dataKeyA}
                fill={colorA}
                name={
                  Object.keys(labelToValueMap || {}).find(
                    (key) => labelToValueMap?.[key] === dataKeyA
                  ) || "Zone based F1 score"
                }
                opacity={opacity?.[dataKeyA] ?? 1}
              />
              <Bar
                barSize={4}
                isAnimationActive={false}
                dataKey={dataKeyB}
                name={
                  Object.keys(labelToValueMap || {}).find(
                    (key) => labelToValueMap?.[key] === dataKeyB
                  ) || "Pin based F1 score"
                }
                fill={colorB}
                opacity={opacity?.[dataKeyB] ?? 1}
              />
            </BarChart>
          ) : (
            <LineChart data={data?.results} onClick={onClickChart}>
              <CartesianGrid stroke="#eee" />
              <XAxis
                dataKey="requested_at"
                className="tabular-nums"
                tickFormatter={(value) =>
                  dayjs(value).utc().format("YYYY-MM-DD, HH:mm")
                }
                fontSize={11}
                angle={-45}
                tickLine
                tickMargin={10}
                tick={{ textAnchor: "end" }}
                height={110}
                overflow="visible"
              />
              <YAxis fontSize={12} />
              <Tooltip content={tooltipContent} />
              <Legend
                fontSize={10}
                fontSizeAdjust={0.5}
                onClick={onClickLegend}
                formatter={legendFormatter}
              />
              <Line
                strokeWidth={2}
                isAnimationActive={false}
                type="monotone"
                dataKey={dataKeyA}
                stroke={colorA}
                name={
                  Object.keys(labelToValueMap || {}).find(
                    (key) => labelToValueMap?.[key] === dataKeyA
                  ) || "Zone based F1 score"
                }
                activeDot={{
                  r: opacity && opacity[dataKeyA] > 0 ? 6 : 0,
                }}
                dot={renderDot}
                opacity={opacity?.[dataKeyA] ?? 1}
                connectNulls
                strokeOpacity={opacity?.[dataKeyA] ?? 1}
              />
              <Line
                isAnimationActive={false}
                strokeWidth={2}
                activeDot={{
                  r: opacity && opacity[dataKeyB] > 0 ? 6 : 0,
                }}
                dot={renderDot}
                type="monotone"
                dataKey={dataKeyB}
                name={
                  Object.keys(labelToValueMap || {}).find(
                    (key) => labelToValueMap?.[key] === dataKeyB
                  ) || "Pin based F1 score"
                }
                stroke={colorB}
                opacity={opacity?.[dataKeyB] ?? 1}
                connectNulls
                strokeOpacity={opacity?.[dataKeyB] ?? 1}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      )}
    </Flex>
  );
}
